import {zodResolver} from '@hookform/resolvers/zod';
import {isFrozenTaskDate} from '@nursio-link/common/utils';
import {useEffect} from 'react';
import {useForm, useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';

import {Button} from '@/components/ui/button';
import type {DialogProps} from '@/components/ui/dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {Form as FormProvider, FormRequiredLegend} from '@/components/ui/form';
import {FormSkeleton} from '@/components/ui/skeleton';
import {useToast} from '@/components/ui/use-toast';
import {graphqlClient} from '@/services/graphqlClient';
import type {Task} from '@/services/graphqlHooks';
import {
  useCompleteTaskMutation,
  useGetTaskQuery,
  useRejectTaskMutation,
} from '@/services/graphqlHooks';

import type {TaskActionFormValues} from '../../utils/tasksFormInputs';
import {
  taskActionFormDefaultValues,
  taskActionFormSchema,
} from '../../utils/tasksFormInputs';
import {isStandardTask} from '../../utils/tasksHelpers';
import ArchivedTaskNotice from '../ArchivedTaskNotice';
import TaskActionFormFields from '../task-form-fields/TaskActionFormFields';
import TaskFormPreview from '../TaskFormPreview';

export type PreviewTaskDialogProps = DialogProps;

const PreviewTaskDialog = ({
  onOpenChange,
  ...props
}: PreviewTaskDialogProps) => {
  const {t} = useTranslation();
  const {toast} = useToast();

  const [searchParams] = useSearchParams();
  const id = searchParams.get('task') || '';

  const rejectTask = useRejectTaskMutation(graphqlClient);
  const completeTask = useCompleteTaskMutation(graphqlClient);

  const {data: taskData, isLoading: isTaskLoading} = useGetTaskQuery(
    graphqlClient,
    {id},
    {
      enabled: !!(id && props.open),
      staleTime: 0,
      refetchOnMount: 'always',
      initialData: undefined,
    }
  );

  const task = taskData?.getTask as Task;

  const form = useForm<TaskActionFormValues>({
    resolver: zodResolver(taskActionFormSchema),
    defaultValues: taskActionFormDefaultValues,
  });

  const mode = useWatch({control: form.control, name: 'mode'});

  useEffect(() => {
    form.reset(taskActionFormDefaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleTaskAction = async (values: TaskActionFormValues) => {
    try {
      if (values.mode === 'reject') {
        await rejectTask.mutateAsync({
          input: {id, rejectionNote: values.rejectionNote || ''},
        });
        toast({title: t('Task rejected'), variant: 'success'});
        onOpenChange?.(true);
        return;
      }

      if (values.mode !== 'comment') return;

      await completeTask.mutateAsync({input: {id, comment: values.comment}});
      toast({title: t('Task completed'), variant: 'success'});
      onOpenChange?.(true);
    } catch (error) {
      console.error(error);
    }
  };

  const isArchived = isFrozenTaskDate(task?.date);
  const showActions = isStandardTask(task) && !task?.completedBy && !isArchived;
  const isPending = rejectTask.isPending || completeTask.isPending;

  const handleOpenChange = (open: boolean) => {
    if (!open) form.reset(taskActionFormDefaultValues);
    onOpenChange?.(open);
  };

  return (
    <FormProvider {...form}>
      <Dialog {...props} onOpenChange={handleOpenChange}>
        <DialogContent asChild>
          <form onSubmit={form.handleSubmit(handleTaskAction)} noValidate>
            <DialogHeader className="md:hidden">
              <DialogTitle>{t('Task')}</DialogTitle>
            </DialogHeader>
            <DialogSection className="py-0">
              {isTaskLoading && <FormSkeleton />}
              {!isTaskLoading && !task && <div>{t('Task not found')}</div>}
              {task && (
                <div className="flex flex-col gap-3">
                  <TaskFormPreview task={task} />
                  {isArchived && <ArchivedTaskNotice />}
                  {showActions && (
                    <div className="mt-6 flex flex-col gap-5">
                      {(mode === 'comment' || mode === 'reject') && (
                        <FormRequiredLegend />
                      )}
                      <TaskActionFormFields form={form} />
                    </div>
                  )}
                </div>
              )}
            </DialogSection>
            <DialogFooter>
              {!!task && mode !== 'none' && (
                <>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => handleOpenChange(false)}
                  >
                    {t('Close')}
                  </Button>
                  <Button variant="primary" type="submit" disabled={isPending}>
                    {mode === 'reject' ? t('Reject task') : t('Complete')}
                  </Button>
                </>
              )}
              {(!task || mode === 'none') && (
                <Button
                  variant="primary"
                  type="button"
                  onClick={() => handleOpenChange(false)}
                >
                  {t('Close')}
                </Button>
              )}
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </FormProvider>
  );
};

export default PreviewTaskDialog;
